import { Box, Grid, Typography } from "@mui/material";
import { useCallback } from "react";
import TextField from "../text-field/text-field";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { getExtensionFromName, getMediaTypeFromExtension, hasFileExtension } from "@lib/mime/mime";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import { Tooltip, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, TextField as MuiTextField } from "@mzc-pdc/ui";
import { IconCancel, IconFile } from "@mzc-cloudplex/icons";
import convertToByte from "@cores/convertToByte";
import { LIMIT_CHAR, MEDIA_TYPES, UPLOAD_FILE_LOCATION_TYPE } from "@constants";
import { findElementNameByKey } from "./helpers";

const UploadAssetItemMulti = ({
    files,
    elements,
    elementsByMediaType,
    removeSelectedFile,
    removeAll,
    disabled,
    onChangeFileName,
    onChangeLabel,
    onChangeLanguage,
    onChangeCaptionKind,
    languages,
    validationErrors,
}) => {
    const { t } = useTranslation();
    const isCaptionGroup = useCallback(
        (file) => {
            return [MEDIA_TYPES.CAPTION].includes(getMediaTypeFromExtension(getExtensionFromName(file.name)));
        },
        [files],
    );

    const isImageGroup = useCallback((file) => {
        return [MEDIA_TYPES.IMAGE].includes(getMediaTypeFromExtension(getExtensionFromName(file.name)));
    }, []);

    const isGroupItem = useCallback(
        (file) => {
            return isCaptionGroup(file) || isImageGroup(file);
        },
        [isCaptionGroup, isImageGroup],
    );

    const getFileName = useCallback((file) => {
        if (file.locationType === UPLOAD_FILE_LOCATION_TYPE.LOCAL) return file.displayName;
        else return file.name;
    }, []);

    return (
        <Box sx={{ width: "100%" }}>
            {Object.keys(files).map((key, idx) => {
                return files[key]?.map((file, index) => {
                    return (
                        <>
                            {(!isGroupItem(file) || (isGroupItem(file) && index === 0)) && (
                                <Grid
                                    container
                                    flexDirection={`row`}
                                    justifyContent={`start`}
                                    alignItems={`center`}
                                    mt={index === 0 && idx === 0 ? 0 : 3}
                                >
                                    <AssetThumbnail
                                        variant={"table"}
                                        src={file.accessUrl}
                                        minWidth={40}
                                        margin={"0 16px 0 0"}
                                        width={40}
                                        height={40}
                                        asset={{
                                            mediaType: getMediaTypeFromExtension(getExtensionFromName(file.name)),
                                        }}
                                    />
                                    <AssetTypeFlag
                                        size={"small"}
                                        type={getMediaTypeFromExtension(getExtensionFromName(file.name))}
                                    />

                                    <Grid container mr={1} sx={{ flexBasis: "527px", flex: 1 }} flexDirection={"row"}>
                                        <Grid item>
                                            {isCaptionGroup(file) && (
                                                <FormControl sx={{ mr: 1, width: "200px" }}>
                                                    <InputLabel id="demo-simple-select-required-label">{t(`common::label::Kind`, "Kind")}</InputLabel>
                                                    <Select
                                                        value={elements[key][index].kind}
                                                        id="simple-select-required"
                                                        label={t(`common::label::Kind`, "Kind")}
                                                        placeholder={t("common::label::Select kind", "Select kind")}
                                                        disabled={disabled}
                                                        labelId="simple-select-required-label"
                                                        onChange={(e) => {
                                                            onChangeCaptionKind(key, index, e.target.value);
                                                        }}
                                                        size="medium"
                                                    >
                                                        <MenuItem value={"CAPTION"}>
                                                            {t(`common::label::Caption`)}
                                                        </MenuItem>
                                                        <MenuItem value={"SUBTITLE"}>
                                                            {t(`common::label::Subtitle`)}
                                                        </MenuItem>
                                                        <MenuItem value={"DESCRIPTION"}>
                                                            {t(`common::label::Description`)}
                                                        </MenuItem>
                                                        <MenuItem value={"CHAPTER"}>
                                                            {t(`common::label::Chapter`)}
                                                        </MenuItem>
                                                        <MenuItem value={"METADATA"}>
                                                            {t(`common::label::Metadata`)}
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        <Grid item flex={1}>
                                            <TextField
                                                variant={"outlined"}
                                                fullWidth
                                                value={findElementNameByKey(elementsByMediaType, key)}
                                                error={validationErrors?.[`elements-${key}-name`]?.level}
                                                helperText={validationErrors?.[`elements-${key}-name`]?.message}
                                                disabled={disabled}
                                                maxLength={LIMIT_CHAR.name}
                                                length={(file.displayName || "").length}
                                                sx={{
                                                    ".MuiInputBase-input": { border: "0px" },
                                                }}
                                                onChange={(e) => onChangeFileName(key, null, e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Box sx={{ width: theme.spacing(10) }}>
                                        <Typography
                                            component={`span`}
                                            variant={"body3"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {isGroupItem(file)
                                                ? t(
                                                      `common::label::${getMediaTypeFromExtension(
                                                          getExtensionFromName(file.name),
                                                      )}`,
                                                      getMediaTypeFromExtension(getExtensionFromName(file.name)),
                                                  )
                                                : hasFileExtension(file.name)
                                                ? getExtensionFromName(file.name)
                                                : t(`common::label::None`, "None")}
                                        </Typography>
                                    </Box>
                                    {file.size !== undefined && (
                                        <Divider flexItem orientation={`vertical`} sx={{ margin: `12px 10px` }} />
                                    )}
                                    <Box sx={{ width: theme.spacing(12.5) }}>
                                        <Typography
                                            component={`span`}
                                            variant={"body3"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {convertToByte(elements[key][index]?.size)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Tooltip title={t(`common::label::Remove`, `Remove`)}>
                                            <IconButton
                                                circled
                                                disabled={disabled}
                                                onClick={(e) =>
                                                    isGroupItem(file) ? removeAll(key) : removeSelectedFile(key, index)
                                                }
                                            >
                                                <IconCancel size={16} htmlColor={theme.palette.secondary[600]} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            )}
                            {isImageGroup(file) && (
                                <>
                                    <Grid
                                        container
                                        pl={10.5}
                                        pt={index === 0 ? 2.5 : 1.5}
                                        pr={29}
                                        alignItems={"center"}
                                        gap={"10px"}
                                    >
                                        <AssetThumbnail
                                            variant={"table"}
                                            src={file.accessUrl ?? null}
                                            minWidth={40}
                                            margin={"0 6px 0 0"}
                                            width={40}
                                            height={40}
                                            asset={{
                                                mediaType: getMediaTypeFromExtension(getExtensionFromName(file.name)),
                                            }}
                                        />
                                        <Typography sx={{ flexBasis: "262px", flex: 1, minWidth: "200px" }}>
                                            {getFileName(file)}
                                        </Typography>
                                        <Divider orientation={"vertical"} sx={{ height: "17px" }} />
                                        <Typography
                                            sx={{ minWidth: "40px" }}
                                            component={`span`}
                                            variant={"body3"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {hasFileExtension(file.name) ? getExtensionFromName(file.name) : t(`common::label::None`, "None")}
                                        </Typography>
                                        <Divider orientation={"vertical"} sx={{ height: "17px" }} />
                                        <Typography
                                            sx={{ minWidth: "64px" }}
                                            component={`span`}
                                            variant={"body3"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {convertToByte(elements[key][index]?.size, true)}
                                        </Typography>
                                        <Tooltip title={t(`common::label::Remove`, `Remove`)}>
                                            <IconButton
                                                circled
                                                onClick={(e) => removeSelectedFile(key, index)}
                                                disabled={disabled}
                                            >
                                                <IconCancel size={16} htmlColor={theme.palette.secondary[600]} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </>
                            )}
                            {isCaptionGroup(file) && (
                                <Grid
                                    container
                                    pl={10.5}
                                    pr={"233px"}
                                    pt={index === 0 ? 2.5 : 1.5}
                                    gap={2}
                                    width={"862px"}
                                >
                                    <Grid container alignItems={"center"}>
                                        <Grid item flex={1} sx={{ display: "flex", alignItems: "center" }}>
                                            <IconFile
                                                size={20}
                                                htmlColor={theme.palette.secondary[600]}
                                                sx={{ mr: 1.5 }}
                                            />
                                            <Typography>{file.path}</Typography>
                                        </Grid>
                                        <Divider orientation={"vertical"} sx={{ height: "17px", mx: 1 }} />
                                        <Typography
                                            sx={{ minWidth: "40px" }}
                                            component={`span`}
                                            variant={"body3"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {hasFileExtension(file.name) ? getExtensionFromName(file.name) : t(`common::label::None`, "None")}
                                        </Typography>
                                        <Divider orientation={"vertical"} sx={{ height: "17px", mx: 1 }} />
                                        <Typography
                                            sx={{ minWidth: "64px" }}
                                            component={`span`}
                                            variant={"body3"}
                                            color={theme.palette.secondary[600]}
                                        >
                                            {convertToByte(elements[key][index]?.size)}
                                        </Typography>
                                        <Tooltip title={t(`common::label::Remove`, `Remove`)}>
                                            <IconButton
                                                circled
                                                onClick={(e) => removeSelectedFile(key, index)}
                                                disabled={disabled}
                                            >
                                                <IconCancel size={16} htmlColor={theme.palette.secondary[600]} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid container flexDirection={"row"} gap={1.5}>
                                        <MuiTextField
                                            value={elements[key][index].label}
                                            disabled={disabled}
                                            sx={{ width: "266px" }}
                                            variant={"outlined"}
                                            label={t(`common::label::Label`, "Label")}
                                            placeholder={t(`common::label::Enter Label`, `Enter Label`)}
                                            onChange={(e) => {
                                                onChangeLabel(key, index, e.target.value);
                                            }}
                                        />
                                        <FormControl
                                            sx={{
                                                width: 266,
                                            }}
                                        >
                                            <InputLabel id="demo-simple-select-required-label">{t(`common::label::Language`, "Language")}</InputLabel>
                                            <Select
                                                id="demo-simple-select-required"
                                                label={t(`common::label::Language`, "Language")}
                                                placeholder={t("common::label::Select language", "Select language")}
                                                disabled={disabled}
                                                labelId="demo-simple-select-required-label"
                                                onChange={(e) => {
                                                    const codes = e.target.value.split("-");
                                                    onChangeLanguage(key, index, codes);
                                                }}
                                                size="medium"
                                                defaultValue={`${elements[key][index].language}-${elements[key][index].country}`}
                                            >
                                                {languages?.map((el) => (
                                                    <MenuItem
                                                        key={`upload-asset-item-multi-language-${el.code}`}
                                                        value={el.code}
                                                    >
                                                        {el.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    );
                });
            })}
        </Box>
    );
};

export default UploadAssetItemMulti;
